import { useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Plus, FolderPlus, X } from 'lucide-react';
import { Modal } from '@/shared/components/ui/Modal';
import { Button } from '@/shared/components/ui/Button';
import { Select, Input, MultiSelect } from '@/shared/components/ui';
import { useUser } from '@/shared/hooks/useAuth';
import { getApiErrorMessage } from '@/shared/api/axios';
import { DEPARTMENT_OPTIONS } from '@/features/projects/constants';
import { useTaskMutations } from '../hooks/useTasks';
import type { Task, CreateTaskPayload, UpdateTaskPayload, ProjectOption, UserOption, TaskStatus, TaskPriority } from '../types';

const STATUS_OPTIONS = [
  { value: 'todo', label: 'To Do' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'review', label: 'Review' },
  { value: 'done', label: 'Done' },
];

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

interface TaskFormModalProps {
  open: boolean;
  task?: Task | null; // null / undefined = create mode
  projects: ProjectOption[];
  users: UserOption[];
  isAdmin: boolean;
  loading?: boolean;
  onClose: () => void;
  onSubmit: (payload: CreateTaskPayload | UpdateTaskPayload) => void;
}

type FormState = {
  title: string;
  description: string;
  requirements: string;
  project_id: string;
  assigned_to: string[]; // array of user IDs
  status: TaskStatus;
  priority: TaskPriority;
  due_date: string;
  due_time: string;
  client_name: string;
  client_phone: string;
};

const EMPTY: FormState = {
  title: '',
  description: '',
  requirements: '',
  project_id: '',
  assigned_to: [],
  status: 'todo',
  priority: 'medium',
  due_date: '',
  due_time: '',
  client_name: '',
  client_phone: '',
};

function taskToForm(task: Task): FormState {
  return {
    title: task.title ?? '',
    description: task.description ?? '',
    requirements: task.requirements ?? '',
    project_id: task.project_id ?? '',
    assigned_to: task.assignees?.map((a) => a.id) ?? [],
    status: task.status ?? 'todo',
    priority: task.priority ?? 'medium',
    due_date: task.due_date ?? '',
    due_time: task.due_time ?? '',
    client_name: task.client_name ?? '',
    client_phone: task.client_phone ?? '',
  };
}

export function TaskFormModal({ open, task, projects, users, isAdmin, loading, onClose, onSubmit }: TaskFormModalProps) {
  const isEdit = !!task;
  const [form, setForm] = useState<FormState>(EMPTY);

  // ── Instant-project shortcut (manager+ only, server-enforced) ────────────────
  const user = useUser();
  const { createProject } = useTaskMutations();
  const canCreateProject = ['superadmin', 'admin', 'manager'].includes(user?.role ?? '');
  // ADMIN / MANAGER may only create projects for their own department; SUPER_ADMIN
  // can choose any. Default to the user's department when it's a valid project dept.
  const deptLocked = user?.role === 'admin' || user?.role === 'manager';
  const defaultDept = useMemo(() => {
    const d = user?.department ?? '';
    return DEPARTMENT_OPTIONS.some((o) => o.value === d) ? d : DEPARTMENT_OPTIONS[0].value;
  }, [user?.department]);

  const [showNewProject, setShowNewProject] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectDept, setNewProjectDept] = useState(defaultDept);

  // Reset the form each time the modal opens (or its inputs change), adjusting
  // state during render (via previous-value tracking) instead of in an effect.
  const [prevOpen, setPrevOpen] = useState(open);
  const [prevTask, setPrevTask] = useState(task);
  const [prevDefaultDept, setPrevDefaultDept] = useState(defaultDept);
  if (open !== prevOpen || task !== prevTask || defaultDept !== prevDefaultDept) {
    setPrevOpen(open);
    setPrevTask(task);
    setPrevDefaultDept(defaultDept);
    if (open) {
      setForm(task ? taskToForm(task) : EMPTY);
      setShowNewProject(false);
      setNewProjectName('');
      setNewProjectDept(defaultDept);
    }
  }

  const handleCreateProject = () => {
    const name = newProjectName.trim();
    if (!name) return toast.error('Enter a project name.');
    createProject.mutate(
      { name, department: deptLocked ? defaultDept : newProjectDept },
      {
        onSuccess: (res) => {
          toast.success(`Project "${name}" created.`);
          setForm((prev) => ({ ...prev, project_id: res.id })); // auto-select it
          setShowNewProject(false);
          setNewProjectName('');
        },
        onError: (e) => toast.error(getApiErrorMessage(e, 'Failed to create project.')),
      },
    );
  };

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    const payload: CreateTaskPayload = {
      title: form.title.trim(),
      description: form.description.trim(),
      requirements: form.requirements.trim(),
      project_id: form.project_id || null,
      assigned_to: form.assigned_to,
      status: form.status,
      priority: form.priority,
      due_date: form.due_date || null,
      due_time: form.due_time || undefined,
      client_name: form.client_name.trim(),
      client_phone: form.client_phone.trim(),
    };
    onSubmit(payload);
  };

  const projectOptions = [
    { value: '', label: 'No Project' },
    ...projects.map((p) => ({ value: p.id, label: p.name })),
  ];

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? 'Edit Task' : 'New Task'}
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" form="task-form" loading={loading} disabled={!form.title.trim()}>
            {isEdit ? 'Save Changes' : 'Create Task'}
          </Button>
        </>
      }
    >
      <form id="task-form" onSubmit={handleSubmit} className="space-y-4">
        {/* Title */}
        <Input
          label="Title *"
          name="task-title"
          value={form.title}
          onChange={(e) => set('title', e.target.value)}
          placeholder="Task title…"
          required
        />

        {/* Project + Priority row */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="mb-1.5 flex items-center justify-between">
              <span className="text-body-sm font-medium text-on-surface">Project</span>
              {canCreateProject && !showNewProject && (
                <button
                  type="button"
                  onClick={() => setShowNewProject(true)}
                  className="inline-flex items-center gap-1 text-body-sm font-medium text-primary hover:underline"
                >
                  <FolderPlus className="h-3.5 w-3.5" /> New project
                </button>
              )}
            </div>
            <Select
              name="task-project"
              value={form.project_id}
              onChange={(e) => set('project_id', e.target.value)}
              options={projectOptions}
            />
          </div>
          <Select
            label="Priority"
            name="task-priority"
            value={form.priority}
            onChange={(e) => set('priority', e.target.value as TaskPriority)}
            options={PRIORITY_OPTIONS}
          />
        </div>

        {/* Instant-project shortcut */}
        {showNewProject && (
          <div className="rounded-card border border-dashed border-primary/40 bg-primary-container/20 p-3">
            <div className="mb-2 flex items-center justify-between">
              <span className="inline-flex items-center gap-1.5 text-body-sm font-medium text-on-surface">
                <FolderPlus className="h-4 w-4 text-primary" /> Create an instant project
              </span>
              <button type="button" onClick={() => setShowNewProject(false)} className="text-on-surface-variant hover:text-on-surface" aria-label="Cancel new project">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <Input
                  name="new-project-name"
                  value={newProjectName}
                  onChange={(e) => setNewProjectName(e.target.value)}
                  placeholder="Project name…"
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleCreateProject(); } }}
                />
              </div>
              {!deptLocked && (
                <div className="w-40">
                  <Select
                    name="new-project-dept"
                    value={newProjectDept}
                    onChange={(e) => setNewProjectDept(e.target.value)}
                    options={DEPARTMENT_OPTIONS}
                  />
                </div>
              )}
              <Button type="button" onClick={handleCreateProject} loading={createProject.isPending}>
                <Plus className="h-4 w-4" /> Create
              </Button>
            </div>
            {deptLocked && (
              <p className="mt-1.5 text-body-sm text-on-surface-variant">
                Created in your department ({DEPARTMENT_OPTIONS.find((d) => d.value === defaultDept)?.label ?? defaultDept}).
              </p>
            )}
          </div>
        )}

        {/* Status + timeline */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Select
            label="Status"
            name="task-status"
            value={form.status}
            onChange={(e) => set('status', e.target.value as TaskStatus)}
            options={STATUS_OPTIONS}
          />
          <Input
            label="Due Date"
            name="task-due"
            type="date"
            value={form.due_date}
            onChange={(e) => set('due_date', e.target.value)}
          />
          <Input
            label="Due Time"
            name="task-due-time"
            type="time"
            value={form.due_time}
            onChange={(e) => set('due_time', e.target.value)}
          />
        </div>

        {/* Assignees — multi-select for admin, or read-only for employee */}
        {isAdmin && (
          <MultiSelect
            label="Assign To"
            options={users.map((u) => ({
              value: u.id,
              label: `${u.display_name}${u.department ? ` · ${u.department.replace('_', ' ')}` : ''}`,
            }))}
            value={form.assigned_to}
            onChange={(val) => set('assigned_to', val)}
            placeholder="Select assignees..."
          />
        )}

        {/* Description */}
        <div>
          <label htmlFor="task-desc" className="mb-1.5 block text-body-sm font-medium text-on-surface">
            Description
          </label>
          <textarea
            id="task-desc"
            rows={3}
            value={form.description}
            onChange={(e) => set('description', e.target.value)}
            placeholder="Task details…"
            className="w-full resize-none rounded border border-outline bg-surface px-3 py-2 text-data-md text-on-surface placeholder:text-on-surface-variant/70 focus:outline-none focus:ring-2 focus:ring-primary/40"
          />
        </div>

        {/* Requirements */}
        <div>
          <label htmlFor="task-reqs" className="mb-1.5 block text-body-sm font-medium text-on-surface">
            Requirements
          </label>
          <textarea
            id="task-reqs"
            rows={2}
            value={form.requirements}
            onChange={(e) => set('requirements', e.target.value)}
            placeholder="Acceptance criteria…"
            className="w-full resize-none rounded border border-outline bg-surface px-3 py-2 text-data-md text-on-surface placeholder:text-on-surface-variant/70 focus:outline-none focus:ring-2 focus:ring-primary/40"
          />
        </div>

        {/* Client Info */}
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Client Name"
            name="task-client-name"
            value={form.client_name}
            onChange={(e) => set('client_name', e.target.value)}
            placeholder="John Doe..."
          />
          <Input
            label="Client Phone"
            name="task-client-phone"
            value={form.client_phone}
            onChange={(e) => set('client_phone', e.target.value)}
            placeholder="+1234567890"
          />
        </div>
      </form>
    </Modal>
  );
}
